import type { MethodologyRule, RuleChange, RuleDiff } from "@/api/types";
import { StatusLabel } from "@/components/StatusLabel";
import { Tag } from "@/components/Tag";

/**
 * 方法论规则差异（ADR-004 影响预览）。
 *
 * **这个组件存在的唯一理由是守住一条判据**：
 * `available === false`（没比成）与 `available === true && counts.total === 0`（比成了、没变化）
 * 在线缆上都是「三个清单为空」，长得一模一样。
 * 只有先判 `available` 才能把两者分开；否则一次**比较失败**会被渲染成一次**无影响的升级**——
 * 那是比"没做预览"更坏的结果，因为它给出的是一个看起来可信的结论。
 *
 * 因此这里刻意**不用 `counts.total === 0` 当第一判据**，也刻意不把两个空态的文案写成一回事。
 */

const SOURCE_LABEL: Record<string, string> = {
  disk: "当前磁盘包",
  registered: "已注册版本",
};

/** 规则的展示名：优先 `id`，没有则退回层名（`experiments` 整份是一条，见后端 `rule_key`）。 */
function ruleTitle(rule: MethodologyRule): string {
  const id = rule.id;
  if (typeof id === "string" && id) return id;
  const layer = rule.layer;
  return typeof layer === "string" && layer ? layer : "未命名规则";
}

function ruleStatement(rule: MethodologyRule): string {
  const statement = rule.statement;
  return typeof statement === "string" ? statement : "";
}

function ChangeRow({ change }: { change: RuleChange }) {
  return (
    <li className="activity-item">
      <span className="mini-mark">~</span>
      <div>
        <p>{change.id}</p>
        <small>变更字段：{change.fields.join(" / ")}</small>
      </div>
    </li>
  );
}

function RuleRow({ rule, mark }: { rule: MethodologyRule; mark: string }) {
  const statement = ruleStatement(rule);
  return (
    <li className="activity-item">
      <span className="mini-mark">{mark}</span>
      <div>
        <p>{ruleTitle(rule)}</p>
        <small>{statement || "（无 statement）"}</small>
      </div>
    </li>
  );
}

export interface RuleDiffListProps {
  diff: RuleDiff;
}

export function RuleDiffList({ diff }: RuleDiffListProps) {
  const sources = [
    SOURCE_LABEL[diff.sources.from ?? ""] ?? "未知来源",
    SOURCE_LABEL[diff.sources.to ?? ""] ?? "未知来源",
  ].join(" → ");

  if (!diff.available) {
    return (
      <div className="inline-note" data-testid="rule-diff-unavailable">
        <StatusLabel tone="warning">无法比较</StatusLabel>
        <p style={{ marginTop: 6 }}>
          没有取到两版完整的规则清单，所以这里没有差异清单—— 空清单在这里的含义是「没比成」，
          <strong>不是</strong>「没有变化」。 具体缺哪一版见上方「说明」。
        </p>
      </div>
    );
  }

  const total = diff.counts.total ?? 0;
  if (total === 0) {
    return (
      <div className="inline-note" data-testid="rule-diff-empty">
        <StatusLabel tone="success">无差异</StatusLabel>
        <p style={{ marginTop: 6 }}>
          两版规则清单逐条一致（来源：{sources}），没有需要按新标准复核的规则。
        </p>
      </div>
    );
  }

  return (
    <div data-testid="rule-diff">
      <div className="meta-row" style={{ margin: "10px 0" }}>
        <StatusLabel tone="info">有差异</StatusLabel>
        <Tag tone="green">新增 {diff.counts.added ?? 0}</Tag>
        <Tag tone="red">删除 {diff.counts.removed ?? 0}</Tag>
        <Tag tone="gold">修改 {diff.counts.changed ?? 0}</Tag>
        <span className="inline-note">来源：{sources}</span>
      </div>
      <ul className="activity-list">
        {diff.added.map((rule) => (
          <RuleRow key={`add-${ruleTitle(rule)}`} rule={rule} mark="+" />
        ))}
        {diff.removed.map((rule) => (
          <RuleRow key={`del-${ruleTitle(rule)}`} rule={rule} mark="−" />
        ))}
        {diff.changed.map((change) => (
          <ChangeRow key={`chg-${change.id}`} change={change} />
        ))}
      </ul>
    </div>
  );
}
